import { Layout } from '../components/Layout';
import { VerifyCodeForm } from '../components/VerifyCodeForm';

export function HomePage() {
  return (
    <Layout>
      {/* Hero — restrained, single-column editorial */}
      <section className="max-w-3xl mx-auto px-5 sm:px-8 pt-20 sm:pt-28 pb-24">
        <span className="accent-bar reveal-bar" style={{ ['--delay' as string]: '40ms' }} />
        <p
          className="eyebrow mt-6 reveal"
          style={{ ['--delay' as string]: '120ms' }}
        >
          Sistema de verificación
        </p>
        <h1 className="display-1 mt-3" style={{ fontSize: 'clamp(2.25rem, 4vw + 1rem, 3.75rem)' }}>
          <span className="reveal inline-block" style={{ ['--delay' as string]: '180ms' }}>Verifica</span>{' '}
          <span className="light reveal inline-block" style={{ ['--delay' as string]: '260ms' }}>una credencial</span>
        </h1>
        <p
          className="prose-measure text-[16px] leading-relaxed text-[var(--bi-oscuro)] mt-7 reveal"
          style={{ ['--delay' as string]: '360ms' }}
        >
          Las credenciales emitidas por Bootcamp Institute son verificables públicamente bajo el estándar
          <strong className="font-semibold"> Open Badges 2.0</strong>, firmadas criptográficamente vía AWS KMS RS-256.
        </p>

        <div
          className="mt-12 reveal"
          style={{ ['--delay' as string]: '460ms' }}
        >
          <VerifyCodeForm
            autoFocus
            helperText={
              <>
                ¿Tienes la URL completa <code className="text-[var(--bi-oscuro)] font-mono">verify.bootcamp-institutex.com/BG-…</code>? Ábrela directamente.
              </>
            }
          />
        </div>

        <dl className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4 border-t border-[var(--hairline)] pt-8">
          <Spec label="Estándar" value="Open Badges 2.0" sub="IMS Global" delay={620} />
          <Spec label="Firma" value="JWS RS-256" sub="AWS KMS asymmetric" delay={700} />
          <Spec label="Retención" value="5 años" sub="FOSFEC compliance" delay={780} />
        </dl>
      </section>

      {/* For verifiers — minimal, no dark hero */}
      <section className="border-t border-[var(--hairline)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
          <p className="eyebrow">Para empleadores y ATS</p>
          <h2 className="mt-3 text-[22px] sm:text-[24px] font-semibold text-[var(--bi-oscuro)] tracking-tight">
            Verificación programática
          </h2>
          <p className="mt-3 text-[14px] text-[var(--bi-claro)] leading-relaxed prose-measure">
            Cada credencial expone un endpoint JSON-LD bajo Open Badges 2.0 que cualquier sistema externo puede consumir.
          </p>
          <pre className="mt-6 bg-[var(--surface-tint)] border border-[var(--border)] text-[var(--bi-oscuro)] p-5 rounded-md overflow-x-auto text-[12px] sm:text-[13px] leading-relaxed font-mono">
{`curl -H "Accept: application/ld+json" \\
  https://verify.bootcamp-institutex.com/api/v1/verify/{code}`}
          </pre>
          <p className="mt-3 text-[11px] text-[var(--bi-claro)]">
            Rate limit: 100 req/min por IP.
          </p>
        </div>
      </section>

      {/* Privacy — minimal */}
      <section className="border-t border-[var(--hairline)]">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
          <p className="eyebrow">Privacidad</p>
          <h2 className="mt-3 text-[22px] sm:text-[24px] font-semibold text-[var(--bi-oscuro)] tracking-tight">
            Habeas Data
          </h2>
          <p className="mt-3 text-[14px] text-[var(--bi-oscuro)] leading-relaxed prose-measure">
            La identidad del portador se almacena hasheada con salt único (SHA-256), en cumplimiento de
            LFPDPPP (México) y Ley 1581/2012 (Colombia). El portador puede retirar su consentimiento de
            verificación pública en cualquier momento; la URL responderá con{' '}
            <code className="text-[var(--bi-claro)] font-mono text-[12px]">410 Gone</code>.
          </p>
        </div>
      </section>
    </Layout>
  );
}

function Spec({ label, value, sub, delay = 0 }: { label: string; value: string; sub: string; delay?: number }) {
  return (
    <div className="reveal" style={{ ['--delay' as string]: `${delay}ms` }}>
      <dt className="eyebrow">{label}</dt>
      <dd className="mt-1.5">
        <div className="text-[14px] font-semibold text-[var(--bi-oscuro)] leading-tight">{value}</div>
        <div className="text-[11px] text-[var(--bi-claro)] mt-0.5">{sub}</div>
      </dd>
    </div>
  );
}
